import {
  Alert,
  Box,
  Button,
  Header,
  Link,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { listVoiceSalesCalls, listVoiceSalesCampaigns } from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import {
  filterAndSortCalls,
  paginateItems,
  type CallSortingField,
  type CallsCollectionState,
  type SortDirection,
} from './collections';
import { formatDuration, VoiceSalesStatus } from './presentation';
import { RichContext } from './RichContext';

export type CallsSearch = {
  q?: string;
  campaign?: string;
  status?: string;
  sortingField?: CallSortingField;
  sortDirection?: SortDirection;
  page?: number;
};

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Connecting', value: 'provider_initiated' },
  { label: 'In progress', value: 'running' },
  { label: 'Completed', value: 'completed' },
  { label: 'Needs attention', value: 'failed' },
];
const SORT_OPTIONS: Array<{ label: string; value: CallSortingField }> = [
  { label: 'Started', value: 'started' },
  { label: 'Duration', value: 'duration' },
  { label: 'Status', value: 'status' },
  { label: 'Campaign', value: 'campaign' },
];

function statusCode(error: unknown): number {
  return typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: unknown }).status ?? 0)
    : 0;
}

function LabelledValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="span">{children}</Box>
    </div>
  );
}

export default function VoiceSalesCallsPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CallsSearch;
  const state: CallsCollectionState = {
    q: search.q ?? '',
    campaign: search.campaign ?? '',
    status: search.status ?? '',
    sortingField: search.sortingField ?? 'started',
    sortDirection: search.sortDirection ?? 'desc',
  };
  const page = Math.max(1, Number(search.page) || 1);

  const campaignsQuery = useQuery({
    queryKey: ['voice-sales-campaigns'],
    queryFn: () => listVoiceSalesCampaigns(),
  });
  const callsQuery = useQuery({
    queryKey: ['voice-sales-calls', 'index'],
    queryFn: () => listVoiceSalesCalls(),
    refetchInterval: 15_000,
  });

  const campaigns = campaignsQuery.data ?? [];
  const campaignNames = new Map(campaigns.map((campaign) => [campaign.id, campaign.name]));
  const filtered = filterAndSortCalls(callsQuery.data ?? [], campaigns, state);
  const paged = paginateItems(filtered, page, PAGE_SIZE);
  const loading = callsQuery.isPending || campaignsQuery.isPending;
  const error = callsQuery.error ?? campaignsQuery.error;
  const denied = statusCode(error) === 403;
  const campaignOptions = [
    { label: 'All campaigns', value: '' },
    ...campaigns.map((campaign) => ({ label: campaign.name, value: String(campaign.id) })),
  ];

  const updateSearch = (updates: Partial<CallsSearch>) => {
    void navigate({
      to: '/dashboard/voice-sales/calls',
      search: { ...search, ...updates },
    });
  };

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Calls', href: '/dashboard/voice-sales/calls' },
      ]}
      contentType="table"
    >
      <Table
        variant="full-page"
        stickyHeader
        stripedRows
        resizableColumns
        wrapLines
        loading={loading}
        loadingText="Loading calls"
        items={denied ? [] : paged.items}
        sortingColumn={{ sortingField: state.sortingField }}
        sortingDescending={state.sortDirection === 'desc'}
        onSortingChange={({ detail }) =>
          updateSearch({
            sortingField: (detail.sortingColumn.sortingField ?? 'started') as CallSortingField,
            sortDirection: detail.isDescending ? 'desc' : 'asc',
            page: 1,
          })
        }
        pagination={
          <Pagination
            currentPageIndex={paged.currentPage}
            pagesCount={paged.pagesCount}
            disabled={loading}
            onChange={({ detail }) => updateSearch({ page: detail.currentPageIndex })}
          />
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <TextFilter
              filteringText={state.q}
              filteringPlaceholder="Search calls"
              filteringAriaLabel="Search calls"
              countText={`${filtered.length} loaded result${filtered.length === 1 ? '' : 's'}`}
              onChange={({ detail }) => updateSearch({ q: detail.filteringText || undefined, page: 1 })}
            />
            <Select
              ariaLabel="Campaign"
              selectedOption={
                campaignOptions.find((option) => option.value === state.campaign) ?? campaignOptions[0]
              }
              options={campaignOptions}
              onChange={({ detail }) =>
                updateSearch({ campaign: detail.selectedOption.value || undefined, page: 1 })
              }
            />
            <Select
              ariaLabel="Status"
              selectedOption={STATUS_OPTIONS.find((option) => option.value === state.status) ?? STATUS_OPTIONS[0]}
              options={STATUS_OPTIONS}
              onChange={({ detail }) =>
                updateSearch({ status: detail.selectedOption.value || undefined, page: 1 })
              }
            />
            <Select
              ariaLabel="Sort calls by"
              selectedOption={SORT_OPTIONS.find((option) => option.value === state.sortingField) ?? SORT_OPTIONS[0]}
              options={SORT_OPTIONS}
              onChange={({ detail }) =>
                updateSearch({ sortingField: detail.selectedOption.value as CallSortingField, page: 1 })
              }
            />
          </SpaceBetween>
        }
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {denied ? (
              <Alert type="error" header="You do not have access to Voice Sales.">
                Ask an internal administrator if you believe you need access.
              </Alert>
            ) : error ? (
              <SpaceBetween size="s">
                <Box fontWeight="bold">Calls could not be loaded</Box>
                <Button onClick={() => void Promise.all([callsQuery.refetch(), campaignsQuery.refetch()])}>
                  Retry
                </Button>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <Box fontWeight="bold">No calls match these filters.</Box>
                <Box variant="p" color="text-body-secondary">
                  Clear search or filters to review all loaded calls.
                </Box>
              </SpaceBetween>
            )}
          </Box>
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={`(${filtered.length})`}
            description="Search and sort the latest 100 loaded calls. Updates every 15 seconds."
            actions={
              <Button iconName="refresh" loading={callsQuery.isFetching} onClick={() => callsQuery.refetch()}>
                Refresh
              </Button>
            }
          >
            Calls
          </Header>
        }
        columnDefinitions={[
          {
            id: 'contact',
            header: 'Contact',
            cell: (call) => (
              <RichContext
                context={call.context}
                phoneNumber={call.display_phone_number}
                lineTypeInfo={call.phone_line_type_info}
                sections={['contact']}
              />
            ),
            minWidth: 230,
          },
          {
            id: 'business',
            header: 'Business & scorecard',
            cell: (call) => (
              <RichContext context={call.context} sections={['business', 'scorecard']} />
            ),
            minWidth: 310,
          },
          {
            id: 'campaign',
            header: 'Campaign',
            cell: (call) => (
              <SpaceBetween direction="vertical" size="xxxs">
                {call.campaign?.uuid ? (
                  <Link
                    href={`/dashboard/voice-sales/campaigns/${call.campaign.uuid}`}
                    onFollow={(event) => {
                      event.preventDefault();
                      void navigate({
                        to: '/dashboard/voice-sales/campaigns/$campaignUuid',
                        params: { campaignUuid: call.campaign?.uuid ?? '' },
                      });
                    }}
                  >
                    {call.campaign.name}
                  </Link>
                ) : (
                  <Box variant="span">
                    {call.campaign?.name ?? campaignNames.get(call.campaign_id) ?? 'Campaign unavailable'}
                  </Box>
                )}
                <Link
                  href={`/dashboard/voice-sales/calls/${call.uuid}`}
                  onFollow={(event) => {
                    event.preventDefault();
                    void navigate({
                      to: '/dashboard/voice-sales/calls/$callUuid',
                      params: { callUuid: call.uuid },
                    });
                  }}
                  variant="secondary"
                >
                  Call record
                </Link>
              </SpaceBetween>
            ),
            sortingField: 'campaign',
            minWidth: 170,
          },
          {
            id: 'outcome',
            header: 'Outcome & actions',
            cell: (call) => (
              <SpaceBetween direction="vertical" size="xs">
                <LabelledValue label="Status">
                  <VoiceSalesStatus status={call.status} error={call.error} />
                </LabelledValue>
                <LabelledValue label="Outcome">
                  <VoiceSalesStatus status={call.outcome} />
                </LabelledValue>
                <div>
                  <Box variant="awsui-key-label">Recent actions</Box>
                  {call.recent_actions?.length ? (
                    <SpaceBetween direction="vertical" size="xxxs">
                      {call.recent_actions.map((action) => (
                        <div key={`${action.created_at}-${action.summary}`}>
                          <Box variant="span">{action.summary}</Box>
                          <Box variant="small" color="text-body-secondary">
                            {formatDateStringWithTime(action.created_at)}
                          </Box>
                        </div>
                      ))}
                    </SpaceBetween>
                  ) : (
                    <Box color="text-body-secondary">No recent actions</Box>
                  )}
                </div>
              </SpaceBetween>
            ),
            sortingField: 'status',
            minWidth: 220,
          },
          {
            id: 'timing',
            header: 'Timing',
            cell: (call) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <LabelledValue label="Started">
                  {call.started_at ? formatDateStringWithTime(call.started_at) : 'Not started'}
                </LabelledValue>
                <LabelledValue label="Duration">{formatDuration(call.duration_seconds)}</LabelledValue>
                <LabelledValue label="Scheduled">
                  {call.scheduled_at ? formatDateStringWithTime(call.scheduled_at) : 'Not scheduled'}
                </LabelledValue>
                <LabelledValue label="Ended">
                  {call.completed_at ? formatDateStringWithTime(call.completed_at) : 'Not ended'}
                </LabelledValue>
              </SpaceBetween>
            ),
            sortingField: 'started',
            minWidth: 190,
          },
        ]}
      />
    </DashboardViewLayout>
  );
}
